import { FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import QuoteMark from 'design-system/Iconography/SVG/QuoteMark'
import HomeListerSVGLogo from 'design-system/Logo/HomeListerSVGLogo'
import Body1 from 'design-system/Typography/Body/Body1'
import Body4 from 'design-system/Typography/Body/Body4'
import Caption from 'design-system/Typography/Caption/Caption'
import Heading2 from 'design-system/Typography/Heading/Heading2'
import Heading4 from 'design-system/Typography/Heading/Heading4'

import Image from 'components/Image'
import Spacer from 'components/Spacer'
import ViewportMarginContainer from 'components/ViewportMarginContainer'

const BACKGROUND_COLOR = '80c8e1'

const data = [
  {
    title: 'Tuesday',
    content: 'Selected our Premium package',
  },
  {
    title: 'Following Tuesday',
    content: 'Listed her home on the MLS',
  },
  {
    title: 'Wednesday',
    content: 'Had 6 offers',
  },
  {
    title: 'Friday',
    content: 'Was under contract for over list price',
  },
  {
    title: '30 Days Later',
    content: 'Closed escrow and headed out for the vacation of a lifetime with the savings',
  },
]

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const Background = styled.div`
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${props => props.theme.colors.teal500.alpha(0.2)};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    background-image: url(${process.env
    .CDN_BASE_URL}/e_make_transparent:4/b_rgb:${BACKGROUND_COLOR}/q_auto,c_crop,w_1,x_0.2,y_0.32/amazon/assets/how-it-works/case-study-bg.jpg);
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    background-image: url(${process.env
    .CDN_BASE_URL}/e_make_transparent:4/b_rgb:${BACKGROUND_COLOR}/q_auto,c_crop,w_1,h_0.7,x_0.25,y_0.3/amazon/assets/how-it-works/case-study-bg.jpg);
  }

  @media (min-width: 1680px) {
    background-size: contain;
    background-color: #${BACKGROUND_COLOR}; /* exception to cover the empty background that background-image cannot cover for larger views */
  }
`

const MobileImageContainer = styled.div`
  display: block;
  margin: 32px 0;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    display: none;
  }
`

const Body4Layout = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0 auto;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    max-width: 672px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    max-width: 944px;
  }
`

const QuoteLayout = styled.div`
  display: flex;
  justify-content: flex-end;
  .svg-icon {
    align-items: flex-start;
  }
  svg {
    width: 32px;
    height: 24px;
    @media (min-width: ${props => props.theme.metrics.tablet}px) {
      width: 48px;
      height: 36px;
    }
    @media (min-width: ${props => props.theme.metrics.desktop}px) {
      width: 64px;
      height: 48px;
    }
  }
`

const QuoteTextContainer = styled(FlexColumn)`
  width: 100%;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: 50%;
  }
`

const QuoteText = styled.div`
  color: ${props => props.theme.colors.neutral900};
  font-size: ${props => props.theme.fonts.sizes[18]}px;
  font-weight: ${props => props.theme.fonts.weights.black};
  line-height: 140%;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: ${props => props.theme.fonts.sizes[22]}px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: ${props => props.theme.fonts.sizes[24]}px;
  }
`

const CardLayout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
  @media (min-width: ${props => props.theme.metrics.desktopLarge}px) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
  }
`

const Card = styled.div`
  flex-basis: 100%;
  margin: 8px 0;
  padding: 24px;
  background-color: ${props => props.theme.colors.white.alpha(0.9)};
  border-radius: ${props => props.theme.radii[24]}px;
  box-shadow: ${props => props.theme.shadows.neutral.level2};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-basis: 30%;
    margin: 15px 12px;
  }
`
const ChartLayoutContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 475px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    height: auto;
  }
`

const ChartMarginContainer = styled.div`
  width: 100%;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    margin: 0 ${props => props.theme.spacings[48]}px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    max-width: ${props => props.theme.metrics.contentMaxWidth}px;
  }
`

const ChartWrapper = styled.div`
  width: 100%;
  position: absolute;
  overflow-x: auto;
  padding-bottom: 24px;

  &::-webkit-scrollbar {
    height: 8px;
    border-radius: 4px;
    background: ${props => props.theme.colors.neutral100};
    margin: 0 50px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.blue500};
    border-radius: 4px;
  }
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    position: relative;
    padding: 0;
    width: 100%;
  }
`

const Chart = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background-color: ${props => props.theme.colors.white};
  border-radius: 24px;
  min-width: 500px;
  margin: 0 32px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    border-radius: 24px 24px 0 0;
    padding: 0 24px;
    margin: 0 30px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 0 32px;
    margin: 0 40px;
  }
`

interface ISelectedProp {
  selected?: boolean
}

const ChartColumn = styled.div<ISelectedProp>`
  display: grid;
  grid-template-rows: 100px repeat(5, minmax(70px, 1fr));
  ${props => props.selected && `background-color: ${props.theme.colors.teal50};`}
`

const ChartTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: ${props => props.theme.fonts.sizes[20]}px;
  font-weight: ${props => props.theme.fonts.weights.heavy};
  padding: 12px 24px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: ${props => props.theme.fonts.sizes[24]}px;
  }
`

interface ICenterProps {
  centerContent?: boolean
}

const ChartItem = styled.div<ICenterProps>`
  display: flex;
  justify-content: ${props => (props.centerContent ? 'center' : 'flex-start')};
  align-items: center;
  padding: 14px 24px 12px 24px;
  border-bottom: 1px solid ${props => props.theme.colors.neutral100};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    height: 70px;
  }
`

const ChartItemXL = styled(ChartItem)`
  border-bottom: none;
`

const ChartItemText = styled.div<ISelectedProp>`
  font-size: ${props => props.theme.fonts.sizes[20]}px;
  font-weight: ${props => props.theme.fonts.weights.black};
  ${props => props.selected && `color: ${props.theme.colors.blue500};`}
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: ${props => props.theme.fonts.sizes[22]}px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: ${props => props.theme.fonts.sizes[24]}px;
  }
`

const HomeListerLogo = styled(HomeListerSVGLogo)`
  svg {
    width: 120px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    svg {
      width: 180px;
    }
  }
`

const HighLightBox = styled.div`
  background-color: ${props => props.theme.colors.blue};
  border-radius: ${props => props.theme.radii[24]}px;
  box-shadow: ${props => props.theme.shadows.neutral.level3};
  padding: 32px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 42px 120px;
  }
`

const HighLightBoxText = styled.div<ISelectedProp>`
  font-size: ${props => props.theme.fonts.sizes[20]}px;
  font-weight: ${props => props.theme.fonts.weights.heavy};
  color: ${props => props.theme.colors.white};
  text-align: center;
  line-height: 27px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: ${props => props.theme.fonts.sizes[24]}px;
    line-height: 33px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: ${props => props.theme.fonts.sizes[30]}px;
    line-height: 41px;
  }
`

const CaseStudy: FunctionComponent = () => {
  const theme = useContext(ThemeContext)

  return (
    <Background>
      <ViewportMarginContainer center>
        <Spacer
          size={theme.spacings[32]}
          tabletSize={theme.spacings[48]}
          desktopSize={theme.spacings[72]}
        />
        <Body1 textAlign="center">Case Study</Body1>
        <Spacer size={theme.spacings[8]} />
        <Heading2 textAlign="center">Changing the Way You Do Real Estate</Heading2>
        <Spacer size={theme.spacings[8]} />
        <Body4Layout>
          <Body4 textAlign="center">
            Meet Diane, she wanted to sell her 3 bedroom house in San Jose, California but
            didn&apos;t want to pay a significant portion of her home&apos;s value to traditional
            real estate agents. So she hired HomeLister.
          </Body4>
        </Body4Layout>
        <MobileImageContainer>
          <Image
            imgSrc={`${process.env.CDN_BASE_URL}/c_crop,w_0.42,h_0.26,x_0.32,y_0.4/q_auto,w_720/amazon/assets/how-it-works/case-study-bg.jpg`}
            borderRadius={theme.radii[24]}
          />
        </MobileImageContainer>
        <Spacer
          size={theme.spacings[72]}
          hideMobile
        />
        <QuoteLayout>
          <QuoteMark width="100%" />
          <Spacer size={theme.spacings[12]} />
          <QuoteTextContainer>
            <QuoteText>
              The HomeLister team made the entire process effortless. You don&apos;t have one
              professional, but several, on your side looking out for your best interests. We sold
              our home with no issues and closed early.”
            </QuoteText>
            <Spacer size={theme.spacings[16]} />
            <Body4>— Diane</Body4>
          </QuoteTextContainer>
        </QuoteLayout>
        <Spacer
          responsiveSize={theme.spacings[32]}
          size={theme.spacings[72]}
        />
        <CardLayout>
          {data.map(({ title, content }) => (
            <Card>
              <Heading4 color="blue700">{title}</Heading4>
              <Spacer size={theme.spacings[12]} />
              <Body4>{content}</Body4>
            </Card>
          ))}
        </CardLayout>
      </ViewportMarginContainer>
      <Spacer
        size={theme.spacings[64]}
        responsiveSize={theme.spacings[16]}
      />
      <ChartLayoutContainer>
        <ChartMarginContainer>
          <ChartWrapper>
            <Chart>
              <ChartColumn>
                <div>&nbsp;</div>
                <ChartItem>Diane&apos;s home selling price</ChartItem>
                <ChartItem>Diane&apos;s fee to selling agent</ChartItem>
                <ChartItem>Diane&apos;s fee to buyer’s agent</ChartItem>
                <ChartItem>Diane&apos;s total spend</ChartItem>
                <ChartItemXL>Diane&apos;s savings</ChartItemXL>
              </ChartColumn>
              <ChartColumn selected>
                <HomeListerLogo />
                <ChartItem centerContent>$1,485,000</ChartItem>
                <ChartItem centerContent>$1,599</ChartItem>
                <ChartItem centerContent>$37,125</ChartItem>
                <ChartItem centerContent>$38,724</ChartItem>
                <ChartItemXL centerContent>
                  <ChartItemText selected>$50,376 😀</ChartItemText>
                </ChartItemXL>
              </ChartColumn>
              <ChartColumn>
                <ChartTitle>Offline Traditional Agent</ChartTitle>
                <ChartItem centerContent>$1,485,000</ChartItem>
                <ChartItem centerContent>$44,550</ChartItem>
                <ChartItem centerContent>$44,550</ChartItem>
                <ChartItem centerContent>$89,100</ChartItem>
                <ChartItemXL centerContent>
                  <ChartItemText>$0 😢</ChartItemText>
                </ChartItemXL>
              </ChartColumn>
            </Chart>
          </ChartWrapper>
        </ChartMarginContainer>
      </ChartLayoutContainer>
      <Spacer
        size={theme.spacings[32]}
        hideTablet
        hideDesktop
      />
      <ViewportMarginContainer center>
        <HighLightBox>
          <HighLightBoxText>
            By using HomeLister over a traditional agent, Diane saved over $50K and is one happy
            homeseller!
          </HighLightBoxText>
        </HighLightBox>
        <Spacer size={theme.spacings[32]} />
        <Caption textAlign="center">
          While this is a real client&apos;s experience with HomeLister, this is not a guarantee of
          performance. Results may vary according to your local market conditions.
        </Caption>
        <Spacer
          size={theme.spacings[32]}
          tabletSize={theme.spacings[48]}
          desktopSize={theme.spacings[72]}
        />
      </ViewportMarginContainer>
    </Background>
  )
}

export default CaseStudy
